import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getToken, base_url } from "../../config/config";

const MainAggregation = () => {
  const [data, setData] = useState([]);
  const [spalte1Values, setSpalte1Values] = useState([]);
  const [isANRDataFetched, setIsANRDataFetched] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [selectedANR, setSelectedANR] = useState(null);
  const token = getToken();

  // Fetch ANR values from the first API
  useEffect(() => {
    const fetchANRValues = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getBulkreport`, {});
        const anrData = response.data.data.map((item) => ({
          ANR: item.ANR,
          invoiceNo: item.invoiceNo,
          einzelpreis: item.Einzelpreis,
          costByUser: item.costByUser,
          profitOrLoss: item.profitOrLoss,
          totalHours: item.totalHours,
          users: item.users,
        }));
        setSpalte1Values(anrData);
        setIsANRDataFetched(true); // Enable the button
      } catch (error) {
        console.error("Error fetching ANR values:", error);
      }
    };

    fetchANRValues();
  }, []);

  // Function to fetch data from the main API using the spalte1Values
  // Function to fetch percentDone for a given ANR
  const fetchPercentDone = async (anr) => {
    try {
      const response = await axios.get(
        `${base_url}/v1/getProjectPercentbyANR/${anr}`,
      );
      if (response.data.status === 1 && response.data.data.length > 0) {
        return response.data.data[0].percentDone;
      } else {
        return "No data"; // Or a default value like "0%"
      }
    } catch (error) {
      console.error(`Error fetching percentDone for ANR ${anr}:`, error);
      return "Error";
    }
  };

  // Function to fetch data from the main API using the spalte1Values
  const fetchData = async () => {
    setIsFetching(true); // Disable the button
    try {
      const response = await axios.post(
        "http://35.159.43.224:3080/m1/data/details",
        { spalte1Values: spalte1Values.map((item) => item.ANR) },
      );
      const fetchedData = response.data.map(async (item) => {
        return {
          ...item,
          data: await Promise.all(
            item.data.map(async (row) => {
              const matchingANR = spalte1Values.find(
                (sp) => sp.ANR === row.spalte1,
              );
              const percentDone = await fetchPercentDone(row.spalte1); // Fetch percentDone for each ANR
              return {
                ...row,
                invoiceNo: matchingANR?.invoiceNo || "kein Datensatz gefunden",
                einzelpreis:
                  matchingANR?.einzelpreis || "kein Datensatz gefunden",
                costByUser:
                  matchingANR?.costByUser || "kein Datensatz gefunden",
                profitOrLoss:
                  matchingANR?.profitOrLoss || "kein Datensatz gefunden",
                totalHours:
                  matchingANR?.totalHours || "kein Datensatz gefunden",
                users: matchingANR?.users || [],
                percentDone: percentDone || "0%", // Add percentDone to row data
              };
            }),
          ),
        };
      });

      const resolvedData = await Promise.all(fetchedData); // Resolve all promises
      // Include ANRs from getBulkreport API if they don't exist in fetchedData
      const existingANRs = new Set(
        resolvedData.flatMap((item) => item.data).map((row) => row.spalte1),
      );
      const additionalData = spalte1Values
        .filter((item) => !existingANRs.has(item.ANR))
        .map((item) => ({
          spalte1: item.ANR,
          azSum: parseFloat(item.totalHours) || 0,
          azSumMultiplied: (parseFloat(item.totalHours) || 0) * 60,
          invoiceNo: item.invoiceNo,
          einzelpreis: item.einzelpreis,
          profitOrLoss:
            parseFloat(item.profitOrLoss) || "kein Datensatz gefunden",
          costByUser: item.costByUser,
          users: item.users,
          percentDone: "No data", // Default value for additional data
        }));

      setData([
        ...resolvedData.flatMap((item) => item.data),
        ...additionalData,
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsFetching(false); // Re-enable the button
    }
  };

  // Get unique ANR values and calculate the sum of AZ and profit/loss
  const uniqueData = useMemo(() => {
    const uniqueANRs = {};
    data.forEach((row, index) => {
      if (!uniqueANRs[row.spalte1]) {
        uniqueANRs[row.spalte1] = {
          ...row,
          rowNumber: index + 1, // Assign row number here
          azSum: 0,
          azSumMultiplied: 0,
          profitOrLossAbdul: "kein Datensatz gefunden",
          profitOrLossMichael: "kein Datensatz gefunden", // Initialize the Profit/Loss (MICHAEL) field
        };
      }
      uniqueANRs[row.spalte1].azSum += row.az || 0;
      uniqueANRs[row.spalte1].azSumMultiplied =
        uniqueANRs[row.spalte1].azSum * 60;

      // Calculate Profit/Loss (Abdul)
      const costByUser = parseFloat(uniqueANRs[row.spalte1].costByUser);
      const priceNatasha = parseFloat(uniqueANRs[row.spalte1].einzelpreis);
      uniqueANRs[row.spalte1].profitOrLossAbdul = isNaN(
        priceNatasha - costByUser,
      )
        ? "kein Datensatz gefunden"
        : priceNatasha - costByUser;

      // Calculate Profit/Loss (MICHAEL)
      const hourCostMichael = uniqueANRs[row.spalte1].azSumMultiplied;
      uniqueANRs[row.spalte1].profitOrLossMichael =
        uniqueANRs[row.spalte1].azSum === 0 ||
        uniqueANRs[row.spalte1].azSum === null
          ? "kein Datensatz gefunden"
          : isNaN(priceNatasha - hourCostMichael)
          ? "kein Datensatz gefunden"
          : priceNatasha - hourCostMichael;
    });
    return Object.values(uniqueANRs);
  }, [data]);

  const columns = useMemo(
    () => [
      {
        header: "Row Number",
        accessorKey: "rowNumber",
        Cell: ({ cell }) => cell.row.index + 1, // Display row number
      },
      {
        header: "ANR",
        accessorKey: "spalte1",
        Cell: ({ cell }) => (
          <Button onClick={() => handleRowClick(cell.getValue())}>
            {cell.getValue()}
          </Button>
        ),
      },
      {
        header: "GESAMTSTUNDEN (EXCEL-MICHAEL)",
        accessorKey: "azSum",
      },
      {
        header: "Stundenkosten (EXCEL-MICHAEL) X 60€/std",
        accessorKey: "azSumMultiplied",
      },
      {
        header: "PREIS (NATASHA)",
        accessorKey: "einzelpreis",
      },
      {
        header: "Rechning nummer (NATASHA)",
        accessorKey: "invoiceNo",
      },
      {
        header: "Kosten pro Arbeitnehmer (Abdul)",
        accessorKey: "costByUser",
      },
      {
        header: "Gewinn/Verlust(Abdul)",
        accessorKey: "profitOrLossAbdul",
        Cell: ({ cell }) => (
          <span
            style={{
              color:
                cell.getValue() === "kein Datensatz gefunden"
                  ? "black"
                  : cell.getValue() >= 0
                  ? "green"
                  : "red",
            }}>
            {cell.getValue() === "kein Datensatz gefunden"
              ? cell.getValue()
              : cell.getValue() >= 0
              ? `Gewinn: ${cell.getValue()}`
              : `Verlust: ${Math.abs(cell.getValue())}`}
          </span>
        ),
      },
      {
        header: "Gewinn/Verlust (MICHAEL)",
        accessorKey: "profitOrLossMichael",
        Cell: ({ cell }) => (
          <span
            style={{
              color:
                cell.getValue() === "kein Datensatz gefunden"
                  ? "black"
                  : cell.getValue() >= 0
                  ? "green"
                  : "red",
            }}>
            {cell.getValue() === "kein Datensatz gefunden"
              ? cell.getValue()
              : cell.getValue() >= 0
              ? `Gewinn: ${cell.getValue()}`
              : `Verlust: ${Math.abs(cell.getValue())}`}
          </span>
        ),
      },
      {
        header: "Abschlussprozentsatz (%)",
        accessorKey: "percentDone", // New column for percentDone
        Cell: ({ cell }) => <span>{cell.getValue()}%</span>,
      },
    ],
    [],
  );

  const handleRowClick = (anr) => {
    const detailedData = data.filter((row) => row.spalte1 === anr);
    setModalData(detailedData);
    setSelectedANR(anr);
    setModalOpen(true);
  };

  const handleClose = () => setModalOpen(false);

  // Function to export data as XLSX
  const exportToXLSX = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      uniqueData.map((row) => {
        return {
          ...row,
          profitOrLossAbdul:
            row.profitOrLossAbdul === "kein Datensatz gefunden"
              ? "kein Datensatz gefunden"
              : row.profitOrLossAbdul,
          profitOrLossMichael:
            row.profitOrLossMichael === "kein Datensatz gefunden"
              ? "kein Datensatz gefunden"
              : row.profitOrLossMichael,
        };
      }),
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Main Data");
    XLSX.writeFile(workbook, "MainData.xlsx");
  };

  // Function to export data as PDF
  const exportToPDF = () => {
    const doc = new jsPDF("landscape");

    doc.text("Main Data", 20, 10);

    const tableBody = uniqueData.map((row, index) => {
      return columns.map((col) => {
        if (
          col.accessorKey === "profitOrLossAbdul" ||
          col.accessorKey === "profitOrLossMichael"
        ) {
          const value = row[col.accessorKey];
          const isProfit = typeof value === "number" && value >= 0;
          return {
            content:
              value === "kein Datensatz gefunden"
                ? value
                : isProfit
                ? `Gewinn: ${value}`
                : `Verlust: ${Math.abs(value)}`,
            styles: {
              fillColor:
                value === "kein Datensatz gefunden"
                  ? [255, 255, 255]
                  : isProfit
                  ? [0, 255, 0]
                  : [255, 0, 0],
              textColor:
                value === "kein Datensatz gefunden"
                  ? [0, 0, 0]
                  : isProfit
                  ? [0, 100, 0]
                  : [139, 0, 0],
            },
          };
        } else if (col.accessorKey === "rowNumber") {
          return index + 1; // Display row number in the PDF
        } else {
          return row[col.accessorKey];
        }
      });
    });

    doc.autoTable({
      head: [columns.map((col) => col.header)],
      body: tableBody,
      styles: { fontSize: 10 }, // Adjust font size as needed
      headStyles: { fillColor: [22, 160, 133] }, // Optional: Style the header
    });

    doc.save("MainData.pdf");
  };

  return (
    <div>
      {isANRDataFetched && (
        <Button
          variant='contained'
          color='primary'
          onClick={fetchData}
          disabled={isFetching} // Disable the button while fetching
        >
          {isFetching ? "Abrufen..." : "Datensätze abrufen"}
        </Button>
      )}
      <Button
        onClick={exportToXLSX}
        variant='contained'
        color='secondary'
        style={{ margin: "10px" }}>
        als Excel exportieren
      </Button>
      <Button
        onClick={exportToPDF}
        variant='contained'
        color='secondary'
        style={{ margin: "10px" }}>
        als PDF exportieren
      </Button>
      <MaterialReactTable
        columns={columns}
        data={uniqueData} // Show unique ANR values with sum of AZ
      />

      <Modal
        open={modalOpen}
        onClose={handleClose}
        aria-labelledby='modal-title'
        aria-describedby='modal-description'>
        <Box
          sx={{
            p: 2,
            backgroundColor: "white",
            margin: "auto",
            marginTop: "10%",
            maxWidth: 800,
          }}>
          <Typography id='modal-title' variant='h6' component='h2'>
            Details for ANR: {selectedANR}
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Total Time</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalData[0]?.users.map((user, index) => (
                  <TableRow key={index}>
                    <TableCell>{user.user}</TableCell>
                    <TableCell>{user.totalTime}</TableCell>
                    <TableCell>{user.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
    </div>
  );
};

export default MainAggregation;
